import React, { useState } from 'react';

const FORMSPREE_URL = 'https://formspree.io/f/mjkoazgy';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setShowThankYou(true);
        setFormData({ name: '', email: '', message: '' });
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-surface-soft">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-ink-900">
            Get In Touch
          </h2>
          <p className="text-lg text-ink-500 max-w-2xl mx-auto">
            Ready to transform your business with technology? Let's discuss your project.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold mb-4 text-ink-900">Email</h3>
              <a 
                href="mailto:dat.luong@3bconsulting.asia" 
                className="text-brand-start hover:text-brand-end transition-colors duration-200 font-medium"
              >
                dat.luong@3bconsulting.asia
              </a>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4 text-ink-900">Phone</h3>
              <p className="text-ink-500">+84 933557726</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4 text-ink-900">Location</h3>
              <p className="text-ink-500">
                43/12 Su Van Hanh Street<br />
                Hoa Hung Ward, Ho Chi Minh City, Vietnam
              </p>
            </div>
          </div>
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="glass-card p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-ink-900 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none focus:ring-2 focus:ring-brand-start/20 transition-colors duration-200"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-ink-900 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none focus:ring-2 focus:ring-brand-start/20 transition-colors duration-200"
                  />
                </div>
              </div>
              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-ink-900 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-ink-300 rounded-lg bg-surface text-ink-700 focus:border-brand-start focus:outline-none focus:ring-2 focus:ring-brand-start/20 transition-colors duration-200 resize-vertical"
                />
              </div>
              {error && <div className="mb-4 text-red-500 text-sm">{error}</div>}
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
      {/* Thank You Modal */}
      {showThankYou && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="glass-card max-w-md w-full text-center p-8 rounded-2xl shadow-2xl border border-white/10 animate-modalSlideIn">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-brand-start to-brand-end rounded-full mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-2 text-ink-900">Thank you!</h3>
              <p className="text-ink-600 mb-6">Your message has been sent. We appreciate your interest and will get back to you soon.</p>
              <button
                className="px-6 py-2 bg-gradient-to-r from-brand-start to-brand-end text-white rounded-lg hover:from-brand-end hover:to-brand-start transition-all duration-300 font-medium"
                onClick={() => setShowThankYou(false)}
              >
                Close
              </button>
            </div>
          </div>
          <style>{`
            @keyframes modalSlideIn {
              from { opacity: 0; transform: translateY(20px) scale(0.95); }
              to { opacity: 1; transform: translateY(0) scale(1); }
            }
            .animate-modalSlideIn {
              animation: modalSlideIn 0.3s cubic-bezier(0.23, 1, 0.32, 1);
            }
          `}</style>
        </div>
      )}
    </section>
  );
};

export default Contact; 